import { useEffect } from 'react'
import { useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet.heat'
import { HeatmapPoint } from '@api/endpoints/marketing'

interface HeatmapLayerProps {
  points: HeatmapPoint[]
  /** Highest weight across the whole dataset, used to normalise intensity. */
  maxWeight: number
  radius?: number
  blur?: number
}

/**
 * Renders the current frame's cells as a Leaflet heat layer. Recreated whenever
 * the points change (i.e. on every playback frame) and cleaned up on unmount.
 */
export function HeatmapLayer({ points, maxWeight, radius = 28, blur = 18 }: HeatmapLayerProps) {
  const map = useMap()

  useEffect(() => {
    const max = maxWeight > 0 ? maxWeight : 1
    const heatPoints: L.HeatLatLngTuple[] = points.map((p) => [
      p.lat,
      p.lng,
      // Floor tiny intensities so sparse cells stay faintly visible.
      Math.max(0.08, p.weight / max),
    ])

    const layer = L.heatLayer(heatPoints, {
      radius,
      blur,
      max: 1.0,
      minOpacity: 0.3,
    })
    layer.addTo(map)

    return () => {
      layer.remove()
    }
  }, [map, points, maxWeight, radius, blur])

  return null
}
